// ===========================================
// COMPONENTE: Dialog de permisos por usuario (override extra/denegados)
// ===========================================

import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Lock, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  aplicarRequeridos,
  calcularPermisosEfectivos,
  FLAGS_REQUIEREN,
  ROL_MULTITENANT_TO_LEGACY,
  ROL_MULTITENANT_CONFIG,
  FLAGS_META_NO_CONCEDIBLES,
  type Permisos,
  type RolMultiTenant,
} from '@/types/roles'
import { useOverridesRol, useActualizarPermisosUsuario } from '@/hooks/useUsuarios'
import type { UsuarioConMembresia } from '@/services/usuariosService'
import { CATEGORIAS_PERMISOS, PERMISO_LABELS } from './permisosCatalogo'

interface PermisosUsuarioDialogProps {
  usuario: UsuarioConMembresia | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

const esMeta = (flag: keyof Permisos): boolean =>
  FLAGS_META_NO_CONCEDIBLES.includes(flag)

export function PermisosUsuarioDialog({
  usuario,
  open,
  onOpenChange,
}: PermisosUsuarioDialogProps) {
  const { data: overrides = [] } = useOverridesRol()
  const actualizar = useActualizarPermisosUsuario()

  // Valores absolutos que el admin quiere para cada flag de este usuario
  const [valores, setValores] = useState<Record<string, boolean>>({})

  const rolMT = (usuario?.rol ?? 'asistente') as RolMultiTenant
  const rolLegacy = ROL_MULTITENANT_TO_LEGACY[rolMT]

  // Overrides org-rol vigentes para el rol del usuario
  const overridesDelRol = overrides
    .filter((o) => o.rol === rolLegacy)
    .reduce<Partial<Record<keyof Permisos, boolean>>>((acc, o) => {
      acc[o.flag as keyof Permisos] = o.permitido
      return acc
    }, {})

  // "Heredado del rol" = base del rol + capa org-rol (sin overrides por-usuario)
  const base = calcularPermisosEfectivos(rolMT, null, null, overridesDelRol)
  // Efectivo actual del usuario (incluye extra/denegados)
  const efectivoUsuario = calcularPermisosEfectivos(
    rolMT,
    (usuario?.permisos_extra ?? null) as Partial<Permisos> | null,
    (usuario?.permisos_denegados ?? null) as (keyof Permisos)[] | null,
    overridesDelRol
  )

  // Inicializar los toggles cuando cambia el usuario o cargan los overrides
  useEffect(() => {
    if (!usuario) return
    setValores({ ...efectivoUsuario })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuario, overrides])

  if (!usuario) return null

  const resetearAlRol = () => setValores({ ...base })

  const guardar = async () => {
    if (!usuario.membresia_id || !usuario.updated_at) {
      toast.error('Usuario sin membresía válida')
      return
    }
    // Derivar extra (concedido sobre la base) y denegados (quitado de la base)
    const extra: Partial<Permisos> = {}
    const denegados: (keyof Permisos)[] = []
    ;(Object.keys(base) as (keyof Permisos)[]).forEach((flag) => {
      if (esMeta(flag)) return
      const quiere = valores[flag] ?? base[flag]
      if (quiere && !base[flag]) extra[flag] = true
      else if (!quiere && base[flag]) denegados.push(flag)
    })
    try {
      await actualizar.mutateAsync({
        membresiaId: usuario.membresia_id,
        extra,
        denegados,
        updatedAt: usuario.updated_at,
        email: usuario.email,
      })
      toast.success('Permisos del usuario actualizados')
      onOpenChange(false)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al actualizar permisos')
    }
  }

  const rolConfig = ROL_MULTITENANT_CONFIG[rolMT]

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg h-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Permisos de {usuario.nombre || usuario.email}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-1">
            Ajustes puntuales sobre el rol
            <Badge variant="outline" className={rolConfig.bgClass}>
              {rolConfig.label}
            </Badge>
            . Marcá o desmarcá para conceder o quitar un permiso a este usuario. Un permiso que
            requiere otro lo concede junto, y quitar el requerido quita los que dependen de él. Los
            permisos con candado no se pueden delegar.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          <div className="space-y-4">
            {Object.entries(CATEGORIAS_PERMISOS).map(([key, categoria]) => (
              <div key={key} className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {categoria.label}
                </p>
                {categoria.permisos.map((flag) => {
                  const meta = esMeta(flag)
                  const quiere = valores[flag] ?? base[flag]
                  const modificado = !meta && quiere !== base[flag]
                  const requerido = FLAGS_REQUIEREN[flag]
                  return (
                    <label
                      key={flag}
                      className={`flex items-center gap-2 py-1 text-sm ${
                        meta ? 'opacity-60' : 'cursor-pointer'
                      }`}
                    >
                      {meta ? (
                        <Lock className="h-4 w-4 text-muted-foreground/60" />
                      ) : (
                        <Checkbox
                          checked={quiere}
                          onCheckedChange={(c) =>
                            setValores((prev) => aplicarRequeridos(prev, flag, c === true).valores)
                          }
                        />
                      )}
                      <span>{PERMISO_LABELS[flag]}</span>
                      {requerido && (
                        <span className="text-xs text-muted-foreground">
                          {`Requiere «${PERMISO_LABELS[requerido]}»`}
                        </span>
                      )}
                      {modificado && (
                        <Badge variant="secondary" className="text-[10px]">
                          modificado
                        </Badge>
                      )}
                    </label>
                  )
                })}
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row justify-between sm:justify-between">
          <Button variant="ghost" onClick={resetearAlRol} disabled={actualizar.isPending}>
            Resetear al rol
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={actualizar.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={guardar} disabled={actualizar.isPending}>
              {actualizar.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
